import { useState } from "react";
import {
  Alert,
  Button,
  Combobox,
  FormField,
  TextInput,
  ToggleInput,
} from "@a2z/ui";
import type { AdminAreaView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useDialogDismiss } from "@/lib/hooks/useDialogDismiss";
import { useAreaGovernoratesQuery } from "../services/useAreaGovernoratesQuery";
import { useCreateAreaMutation } from "../services/useCreateAreaMutation";
import { useUpdateAreaMutation } from "../services/useUpdateAreaMutation";

// Suggestion-approval mode: create a new area pre-filled from a buyer-typed
// suggestion, governorate fixed to the suggestion's, saving calls the
// approve endpoint (not create) so the suggestion gets linked to the area.
type ApproveSuggestion = {
  governorateId: string;
  governorateName: string;
  initialNameAr: string;
  onApprove: (nameAr: string, nameEn: string) => Promise<void>;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  // Present → edit mode (governorate fixed); absent → create mode.
  area?: AdminAreaView;
  approveSuggestion?: ApproveSuggestion;
};

type Draft = {
  governorateId: string;
  nameAr: string;
  nameEn: string;
  isActive: boolean;
};

function initialDraft(area?: AdminAreaView, approveSuggestion?: ApproveSuggestion): Draft {
  if (approveSuggestion) {
    return {
      governorateId: approveSuggestion.governorateId,
      nameAr: approveSuggestion.initialNameAr,
      nameEn: "",
      isActive: true,
    };
  }
  return {
    governorateId: area?.governorateId ?? "",
    nameAr: area?.nameAr ?? "",
    nameEn: area?.nameEn ?? "",
    isActive: area?.isActive ?? true,
  };
}

// Add/edit an area. On edit the governorate is immutable (the PATCH endpoint
// only touches name/isActive), so it renders as a disabled field. When
// `approveSuggestion` is passed, this creates the area via the approve
// endpoint instead of the plain create endpoint (governorate also fixed).
export function AreaFormDialog({ isOpen, onClose, area, approveSuggestion }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const isEdit = area !== undefined;
  const isApprove = approveSuggestion !== undefined;
  const { data: governorates } = useAreaGovernoratesQuery();
  const create = useCreateAreaMutation();
  const updateArea = useUpdateAreaMutation();
  const [draft, setDraft] = useState<Draft>(() =>
    initialDraft(area, approveSuggestion),
  );
  const [error, setError] = useState<string | null>(null);
  const [isApprovePending, setIsApprovePending] = useState(false);
  useDialogDismiss(isOpen, onClose);

  if (!isOpen) return null;

  const isPending = create.isPending || updateArea.isPending || isApprovePending;
  const update = <K extends keyof Draft>(field: K, value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const governorateName = (id: string): string => {
    const gov = (governorates ?? []).find((g) => g.id === id);
    if (!gov) return id;
    return (locale === "ar" ? gov.nameAr : gov.nameEn ?? gov.nameAr) || gov.nameAr;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      if (isApprove) {
        setIsApprovePending(true);
        await approveSuggestion.onApprove(
          draft.nameAr.trim(),
          draft.nameEn.trim(),
        );
      } else if (isEdit) {
        await updateArea.mutateAsync({
          id: area.id,
          nameAr: draft.nameAr.trim(),
          nameEn: draft.nameEn.trim(),
          isActive: draft.isActive,
        });
      } else {
        await create.mutateAsync({
          governorateId: draft.governorateId,
          nameAr: draft.nameAr.trim(),
          nameEn: draft.nameEn.trim() || undefined,
          isActive: draft.isActive,
        });
      }
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiJsonClientError
          ? err.message || t(keys.errors.generic)
          : t(keys.errors.generic),
      );
    } finally {
      if (isApprove) setIsApprovePending(false);
    }
  };

  const submitDisabled =
    isPending ||
    draft.nameAr.trim().length === 0 ||
    (!isEdit && !isApprove && draft.governorateId.length === 0);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 pt-10 pb-[max(2.5rem,env(safe-area-inset-bottom))]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-6 shadow-[var(--shadow-lg)]"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-[color:var(--text-primary)]">
          {isApprove
            ? t(keys.areaSuggestions.approveDialogTitle)
            : isEdit
              ? t(keys.areas.editAction)
              : t(keys.areas.addAction)}
        </h2>
        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
          <FormField
            name="governorateId"
            label={t(keys.areas.governorateLabel)}
            required
          >
            {isEdit || isApprove ? (
              <TextInput
                name="governorateId"
                value={
                  isApprove
                    ? approveSuggestion.governorateName
                    : governorateName(draft.governorateId)
                }
                disabled
              />
            ) : (
              <Combobox
                value={draft.governorateId}
                onChange={(value) => update("governorateId", value ?? "")}
                options={(governorates ?? []).map((gov) => ({
                  value: gov.id,
                  label:
                    (locale === "ar" ? gov.nameAr : gov.nameEn ?? gov.nameAr) ||
                    gov.nameAr,
                }))}
                aria-label={t(keys.areas.governorateLabel)}
              />
            )}
          </FormField>

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              name="nameAr"
              label={t(keys.areas.nameArLabel)}
              required
            >
              <TextInput
                name="nameAr"
                dir="rtl"
                autoFocus
                value={draft.nameAr}
                onChange={(event) => update("nameAr", event.target.value)}
              />
            </FormField>
            <FormField name="nameEn" label={t(keys.areas.nameEnLabel)}>
              <TextInput
                name="nameEn"
                value={draft.nameEn}
                onChange={(event) => update("nameEn", event.target.value)}
              />
            </FormField>
          </div>

          <label className="flex items-center gap-2 text-sm font-medium text-[color:var(--text-secondary)]">
            <ToggleInput
              name="isActive"
              ariaLabel={t(keys.areas.activeLabel)}
              checked={draft.isActive}
              onChange={(event) => update("isActive", event.target.checked)}
            />
            {t(keys.areas.activeLabel)}
          </label>

          {error ? <Alert message={error} /> : null}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
              className="h-9 rounded-md px-3 text-xs font-semibold"
            >
              {t(keys.common.cancel)}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitDisabled}
              className="h-9 rounded-md px-3 text-xs font-semibold"
            >
              {isPending ? t(keys.areas.saving) : t(keys.common.save)}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
